import { parseHtmlString } from "@/utils/helpers/html";
import { Heading, Paragraph } from "@/components/ui/Typography"
import { WebsitePageSection } from "@/types"
import EditableSection from "@/components/admin/Content/WebPages/EditableSection";
import InlineProgrammeEditor from "@/components/admin/Content/WebPages/InlineEditors/InlineProgrammeEditor";
import { useProgrammeHighlights } from "@/lib/api/hooks/programme";
import { Programme } from "@/lib/api/types";

// Helper function to format date
const formatPublishedDate = (dateString: string | null): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
};

const Programmes = ({ data }: { data: WebsitePageSection }) => {
  // Fetch live highlights
  const { data: highlightsData } = useProgrammeHighlights();
  const highlights = highlightsData?.data || [];

  return (
    <EditableSection
      sectionTag={data.sectionTag}
      contentType={data.contentType}
      data={data}
      customEditor={InlineProgrammeEditor}
    >
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <Paragraph className="bg-gray-50 rounded-2xl px-3 py-1 !text-sm flex items-center justify-center text-forestGreen-100 w-fit">
            Programmes ({highlights.length}/2)
          </Paragraph>
          <div className="flex items-center gap-6">
            <Heading level={4}>{data.title ?? "AFSA Programme"}</Heading>
          </div>
          <Paragraph dangerouslySetInnerHTML={parseHtmlString(data.description ?? "Programmes for the landing page")} />
        </div>

        {/* Live Preview */}
        {highlights.length > 0 ? (
          <div className="grid gap-4 md:grid-cols-2">
            {highlights.map((programme: Programme, index: number) => {
              const defaultImage = "https://res.cloudinary.com/dlwegkhme/image/upload/v1755760288/Image-11_sxxnhn.png";

              return (
                <div key={programme.programmeId} className="flex flex-col gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {/* Position Badge */}
                  <div className="inline-flex items-center gap-2">
                    <span className="px-2 py-1 bg-[#681628] text-white text-xs font-medium rounded">
                      Programme {index + 1}
                    </span>
                  </div>

                  {/* Cover Image */}
                  <div className="relative w-full h-32 bg-gray-200 rounded overflow-hidden">
                    <img
                      src={programme.coverImage || defaultImage}
                      alt={programme.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        if (e.currentTarget.src !== defaultImage) {
                          e.currentTarget.src = defaultImage;
                        }
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex flex-col gap-2">
                    <h4 className="font-semibold text-sm text-gray-900 line-clamp-2">{programme.title}</h4>
                    {(programme.datePublished || programme.createdByUsername) && (
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        {programme.datePublished && (
                          <p className="text-xs text-gray-500"><span className="font-medium">Published date:</span> {formatPublishedDate(programme.datePublished)}</p>
                        )}
                        {programme.createdByUsername && (
                          <p className="text-xs text-gray-500"><span className="font-medium">Published by:</span> {programme.createdByUsername}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Tags */}
                  {programme.articleTag && Array.isArray(programme.articleTag) && programme.articleTag.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {programme.articleTag.slice(0, 2).map((tag: string, i: number) => (
                        <span key={i} className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-500 text-sm">No programmes selected. Click edit to select up to 2 programmes.</p>
          </div>
        )}
      </div>
    </EditableSection>
  )
}

export default Programmes